import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { Gesture } from 'react-native-gesture-handler';
import { useSharedValue, withSpring, withTiming } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { PICKER_OPTION_HEIGHT } from './Picker.constants';
import {
  calculateMomentumScrollDistance,
  findClosestOptionIndex,
  getOptionLayoutCoordinates,
  triggerHaptic,
} from './Picker.utils';

import type {
  GestureHandler,
  PickerOptionValue,
  UsePickerGesturesEventsHandlerProps,
  UsePickerHelpersProps,
} from './Picker.types';

// ────────────────────────────────────────────────────────────────────────────────

export const usePickerState = (props: UsePickerHelpersProps) => {
  const { value, onSubmit, options } = props;

  const [selectedOptionValue, setSelectedOptionValue] = useState<PickerOptionValue>(value);

  const handleOnSubmit = useCallback(() => {
    onSubmit(selectedOptionValue);
  }, [onSubmit, selectedOptionValue]);

  const resetSelectedOptionValue = useCallback(() => {
    setSelectedOptionValue(value);
  }, [value]);

  // ────────────────────────────────────────────────────────────────────

  // {selectedOptionLabel} will be changed only after {onSubmit} event
  const selectedOptionLabel = options.find((option) => option.value === value)?.label ?? '';

  // ────────────────────────────────────────────────────────────────────

  // sync external and local values
  useEffect(() => {
    setSelectedOptionValue(value);
  }, [value]);

  // ────────────────────────────────────────────────────────────────────

  return {
    selectedOptionValue,
    selectedOptionLabel,
    setSelectedOptionValue,
    handleSubmit: handleOnSubmit,
    resetSelectedOptionValue,
  };
};

// ────────────────────────────────────────────────────────────────────────────────

export const usePickerGesturesEventsHandler = (props: UsePickerGesturesEventsHandlerProps): any => {
  const { options, optionsWrapperRef, activeOptionIndex, shouldUseScrollVariation, onChange } =
    props;

  const optionsCoordinates = useMemo(() => options.map(getOptionLayoutCoordinates), [options]);

  // ─── DYNAMIC VALUES ─────────────────────────────────────────────────

  const [isInteractionInAction, setIsInteractionInAction] = useState(false);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState(activeOptionIndex);

  const initialSwipeDistance = -(activeOptionIndex * PICKER_OPTION_HEIGHT);
  const swipedDistance = useSharedValue(initialSwipeDistance);

  // Tracks the swipe distance at the start of a gesture (previously the gesture
  // handler `context`, which no longer exists in the v4 `Gesture` API).
  const offsetY = useSharedValue(0);

  // ─── HELPERS ────────────────────────────────────────────────────────

  // As soon as the element is focused, the browser scrolls to that element's position.
  // Therefore, we must reset scroll position, since we "focus" the element by modifying {translateY} property
  // of the wrapper and not the scroll position.
  const restoreWrapperScrollOnFocus = useCallback(() => {
    const wrapper = optionsWrapperRef?.current as unknown as HTMLElement;

    if (Platform.OS !== 'web' || !wrapper || shouldUseScrollVariation) return;
    wrapper?.scrollTo?.({ top: 0 });
  }, [shouldUseScrollVariation, optionsWrapperRef]);

  const selectOptionByIndex = useCallback(
    (optionIndex: number) => {
      const optionLayoutStartCoordinate = optionsCoordinates?.[optionIndex]?.start;
      const shouldPreventFurtherActions =
        optionLayoutStartCoordinate === undefined || isInteractionInAction;

      if (shouldPreventFurtherActions) return;

      swipedDistance.value = withTiming(-optionLayoutStartCoordinate, {
        duration: 150,
      });

      triggerHaptic();
      onChange(options[optionIndex].value);
      restoreWrapperScrollOnFocus();
    },
    [
      onChange,
      isInteractionInAction,
      options,
      optionsCoordinates,
      restoreWrapperScrollOnFocus,
      swipedDistance,
    ],
  );

  const trackPreviousSwipeDistance = useCallback(() => {
    'worklet';

    offsetY.value = swipedDistance.value;
  }, [offsetY, swipedDistance]);

  const trackActiveSwipeDistance = useCallback<GestureHandler>(
    ({ translationY }) => {
      'worklet';

      const swipeDistance = offsetY.value + translationY;
      const closestOptionIndex = findClosestOptionIndex(-swipeDistance, optionsCoordinates);

      swipedDistance.value = swipeDistance;

      scheduleOnRN(setIsInteractionInAction, true);
      scheduleOnRN(setHoveredOptionIndex, closestOptionIndex);
    },
    [offsetY, optionsCoordinates, swipedDistance],
  );

  const swipeToTheClosestOption = useCallback<GestureHandler>(
    ({ translationY: currentDistance, velocityY }) => {
      'worklet';

      const previousDistance = offsetY.value;
      const momentumScrollDistance = calculateMomentumScrollDistance(velocityY);
      const targetSwipeDistance = -(previousDistance + currentDistance + momentumScrollDistance);

      const closestOptionIndex = findClosestOptionIndex(
        targetSwipeDistance,
        optionsCoordinates,
        true,
      );
      const closestOptionValue = options[closestOptionIndex].value;
      const closestOptionStartCoordinate = optionsCoordinates[closestOptionIndex].start;

      // ────────────────────────────────────────────────────────────────────

      const springConfig = { damping: 5000, velocity: velocityY };

      swipedDistance.value = withSpring(-closestOptionStartCoordinate, springConfig);

      scheduleOnRN(setIsInteractionInAction, false);
      scheduleOnRN(onChange, closestOptionValue);
    },
    [offsetY, optionsCoordinates, options, swipedDistance, onChange],
  );

  const animatedGestureHandler = useMemo(
    () =>
      Gesture.Pan()
        .onStart(trackPreviousSwipeDistance)
        .onUpdate(trackActiveSwipeDistance)
        .onEnd(swipeToTheClosestOption),
    [trackPreviousSwipeDistance, trackActiveSwipeDistance, swipeToTheClosestOption],
  );

  // ─── EFFECTS ────────────────────────────────────────────────────────

  useEffect(triggerHaptic, [hoveredOptionIndex]);

  // ────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      swipedDistance,
      animatedGestureHandler,
      selectOptionByIndex,
    }),
    [swipedDistance, animatedGestureHandler, selectOptionByIndex],
  );
};
